import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "container", 
    "itemTemplate", 
    "groupTemplate",
    "modal", 
    "groupModal",
    "requirementName",
    "requirementDescription",
    "requirementType",
    "mandatoryToggle",
    "groupNameInput",
    "groupNameInline",
    "groupDropArea",
    "dropdown",
    "item",
    "group",
    "groupItems",
    "modalTitle",
    "optionsSection",
    "optionsContainer",
    "optionInput"
  ]
  
  static values = {
    dragIcon: String,
    optionTemplate: String
  }
  
  connect() {
    this.draggedElement = null
    this.draggedType = null // 'item' or 'group-item'
    this.updateNumbers()
  }
  
  // Build schema from DOM and submit
  submitForm(event) {
    event.preventDefault()
    const form = event.target
    const schema = this.buildSchemaFromDOM()
    
    // Create hidden field with schema JSON
    const schemaInput = document.createElement('input')
    schemaInput.type = 'hidden'
    schemaInput.name = 'schema'
    schemaInput.value = JSON.stringify(schema)
    form.appendChild(schemaInput)
    
    form.submit()
  }
  
  // Build schema array from DOM structure
  buildSchemaFromDOM() {
    const schema = []
    const children = Array.from(this.containerTarget.children)
    
    children.forEach((child, index) => {
      if (child.classList.contains('requirement-item') && child.style.display !== 'none') {
        // It's a requirement item
        const requirementId = child.querySelector('input[data-requirement-id]')?.value ||
                             child.querySelector('input[name*="funding_program_requirement_id"]')?.value ||
                             child.querySelector('.requirement-select')?.value
        
        if (requirementId) {
          schema.push({
            type: 'item',
            requirement_id: parseInt(requirementId)
          })
        }
      } else if (child.classList.contains('requirement-group') && child.style.display !== 'none') {
        // It's a group
        const groupName = child.dataset.groupName || child.querySelector('.group-name')?.textContent
        const groupItems = child.querySelectorAll('.group-requirement-item')
        const items = []
        
        groupItems.forEach(groupItem => {
          if (groupItem.style.display !== 'none') {
            const requirementId = groupItem.dataset.requirementId
            if (requirementId) {
              items.push({
                type: 'item',
                requirement_id: parseInt(requirementId)
              })
            }
          }
        })
        
        if (groupName) {
          schema.push({
            type: 'group',
            name: groupName,
            items: items
          })
        }
      }
    })
    
    return schema
  }
  
  // Add new requirement item
  addRequirement(event) {
    event.preventDefault()
    const content = this.itemTemplateTarget.innerHTML.replace(/TEMPLATE_RECORD/g, new Date().getTime())
    this.containerTarget.insertAdjacentHTML('beforeend', content)
    this.updateNumbers()
  }

  // Show modal to create new requirement
  showModal(event) {
    event.preventDefault()
    this.modalTarget.classList.add('show')
    document.body.style.overflow = 'hidden'
  }

  // Close requirement modal
  closeModal(event) {
    if (event) event.preventDefault()
    this.modalTarget.classList.remove('show')
    document.body.style.overflow = ''
    this.resetModalForm()
  }

  // Create new requirement via AJAX
  async createRequirement(event) {
    event.preventDefault()
    
    const name = this.requirementNameTarget.value.trim()
    const description = this.requirementDescriptionTarget.value.trim()
    const selectedType = this.requirementTypeTargets.find(radio => radio.checked)
    const mandatory = this.mandatoryToggleTarget.checked
    
    if (!name || !selectedType) {
      alert('Please enter a requirement name and select a type')
      return
    }

    // Collect options if single-select or multi-select
    let options = []
    if (selectedType.value === 'single_select' || selectedType.value === 'multi_select') {
      options = this.optionInputTargets
        .map(input => input.value.trim())
        .filter(value => value.length > 0)
      
      if (options.length === 0) {
        alert('Please add at least one option for single-select or multi-select types')
        return
      }
    }

    const formData = new FormData()
    formData.append('funding_program_requirement[name]', name)
    formData.append('funding_program_requirement[description]', description)
    formData.append('funding_program_requirement[requirement_type]', selectedType.value)
    formData.append('funding_program_requirement[mandatory]', mandatory)
    
    // Add options as JSON array
    if (options.length > 0) {
      formData.append('funding_program_requirement[options]', JSON.stringify(options))
    }

    try {
      const response = await fetch('/funding_programs/create_requirement', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: formData
      })

      const data = await response.json()

      if (data.success) {
        // Add the new requirement to all select dropdowns
        this.addRequirementToSelects(data.requirement)
        this.closeModal()
      } else {
        alert('Error creating requirement: ' + data.errors.join(', '))
      }
    } catch (error) {
      console.error('Error:', error)
      alert('An error occurred while creating the requirement')
    }
  }

  // Add newly created requirement to all select dropdowns
  addRequirementToSelects(requirement) {
    // Add to all existing selects on the page
    const selects = this.element.querySelectorAll('.requirement-select')
    selects.forEach(select => {
      const option = new Option(requirement.name, requirement.id)
      select.add(option)
    })
    
    // Also update the template so new items will have this requirement
    if (this.hasItemTemplateTarget) {
      const templateSelect = this.itemTemplateTarget.content?.querySelector('.requirement-select') ||
                            this.itemTemplateTarget.querySelector('.requirement-select')
      if (templateSelect) {
        const option = new Option(requirement.name, requirement.id)
        templateSelect.add(option)
      }
    }
  }

  // Reset modal form
  resetModalForm() {
    this.requirementNameTarget.value = ''
    this.requirementDescriptionTarget.value = ''
    this.requirementTypeTargets.forEach(radio => radio.checked = false)
    this.mandatoryToggleTarget.checked = true
    this.optionsSectionTarget.style.display = 'none'

    const templateA = this.optionTemplateValue.replace(/\{\{letter\}\}/g, 'A')
    const templateB = this.optionTemplateValue.replace(/\{\{letter\}\}/g, 'B')
    this.optionsContainerTarget.innerHTML = templateA + templateB
  }

  // Toggle options section visibility based on requirement type
  toggleOptionsSection(event) {
    const selectedValue = event.target.value
    const shouldShow = selectedValue === 'single_select' || selectedValue === 'multi_select'
    this.optionsSectionTarget.style.display = shouldShow ? 'block' : 'none'
    
    const templateA = this.optionTemplateValue.replace(/\{\{letter\}\}/g, 'A')
    const templateB = this.optionTemplateValue.replace(/\{\{letter\}\}/g, 'B')
  
    this.optionsContainerTarget.innerHTML = templateA + templateB
  }

  // Add a new option input
  addOption(event) {
    event.preventDefault()
    const currentCount = this.optionInputTargets.length
    const nextLetter = String.fromCharCode(65 + currentCount) // A, B, C, D, etc.
    
    const template = this.optionTemplateValue.replace(/\{\{letter\}\}/g, nextLetter)
    this.optionsContainerTarget.innerHTML += template
  }

  // Show group creation modal
  showGroupModal(event) {
    event.preventDefault()
    this.groupModalTarget.classList.add('show')
    document.body.style.overflow = 'hidden'
  }

  // Close group modal
  closeGroupModal(event) {
    if (event) event.preventDefault()
    this.groupModalTarget.classList.remove('show')
    document.body.style.overflow = ''
    this.groupNameInputTarget.value = ''
  }

  // Save group from modal
  saveGroup(event) {
    event.preventDefault()
    const groupName = this.groupNameInputTarget.value.trim()
    
    if (!groupName) {
      alert('Please enter a group name')
      return
    }

    this.createGroup(groupName)
  }

  // Create group directly (no API call needed)
  createGroup(groupName) {
    // Create new group from template
    const content = this.groupTemplateTarget.innerHTML
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = content
    
    // Update group name in the template
    const groupElement = tempDiv.querySelector('.requirement-group')
    const groupNameSpan = groupElement.querySelector('.group-name')
    if (groupNameSpan) {
      groupNameSpan.textContent = groupName
    }
    
    // Set group name as data attribute
    groupElement.dataset.groupName = groupName
    
    this.containerTarget.insertAdjacentHTML('beforeend', tempDiv.innerHTML)
    this.closeGroupModal()
    this.updateNumbers()
  }

  // Toggle group expansion
  toggleGroup(event) {
    if (event.target.closest('button')) return
    
    const groupElement = event.target.closest('.requirement-group')
    const groupItems = groupElement.querySelector('.group-items')
    const caret = groupElement.querySelector('.group-caret')
    
    groupElement.classList.toggle('collapsed')
    if (groupElement.classList.contains('collapsed')) {
      groupItems.style.display = 'none'
      caret.style.transform = 'rotate(-90deg)'
    } else {
      groupItems.style.display = 'block'
      caret.style.transform = 'rotate(0deg)'
    }
  }

  // Select requirement from dropdown
  selectRequirement(event) {
    const select = event.target
    const wrapper = select.closest('.requirement-select-wrapper')
    
    if (select.value) {
      const selectedText = select.options[select.selectedIndex].text
      wrapper.classList.add('selected')
      wrapper.innerHTML = `
        <span class="requirement-name">${selectedText}</span>
        <input type="hidden" data-requirement-id="${select.value}" value="${select.value}" />
      `
    }
  }

  // Remove individual item
  removeItem(event) {
    event.preventDefault()
    event.stopPropagation()
    const item = event.target.closest('.requirement-item')
    
    const destroyInput = item.querySelector('input[name*="_destroy"]')
    if (destroyInput) {
      destroyInput.value = '1'
      item.style.display = 'none'
    } else {
      item.remove()
    }
    
    this.closeAllDropdowns()
    this.updateNumbers()
  }

  // Remove group
  removeGroup(event) {
    event.preventDefault()
    event.stopPropagation()
    const group = event.target.closest('.requirement-group')
    
    const destroyInput = group.querySelector('input[name*="_destroy"]')
    if (destroyInput) {
      destroyInput.value = '1'
      group.style.display = 'none'
    } else {
      group.remove()
    }
    
    this.closeAllDropdowns()
    this.updateNumbers()
  }

  // Drag and drop functionality
  dragStart(event) {
    this.draggedElement = event.currentTarget
    this.draggedType = this.draggedElement.classList.contains('requirement-item') ? 'item' : 'group'
    event.currentTarget.classList.add('dragging')
    event.dataTransfer.effectAllowed = 'move'
  }

  dragEnd(event) {
    event.currentTarget.classList.remove('dragging')
    this.draggedElement = null
    this.draggedType = null
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    
    if (!this.draggedElement) return
    
    const afterElement = this.getDragAfterElement(this.containerTarget, event.clientY)
    
    if (afterElement == null) {
      this.containerTarget.appendChild(this.draggedElement)
    } else {
      this.containerTarget.insertBefore(this.draggedElement, afterElement)
    }
  }

  drop(event) {
    event.preventDefault()
    this.updateNumbers()
  }

  // Drag over group items area
  dragOverGroup(event) {
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = 'move'
    
    // If dragging a group item, allow reordering within the group
    if (this.draggedElement && this.draggedElement.classList.contains('group-requirement-item')) {
      const groupItems = event.currentTarget
      
      // Remove empty state if it exists
      const emptyState = groupItems.querySelector('.group-empty-state')
      if (emptyState) emptyState.remove()
      
      const afterElement = this.getDragAfterGroupElement(groupItems, event.clientY)
      
      if (afterElement == null) {
        groupItems.appendChild(this.draggedElement)
      } else {
        groupItems.insertBefore(this.draggedElement, afterElement)
      }
    }
  }

  // Drag requirement item to add to group
  dragGroupItem(event) {
    event.stopPropagation() // Prevent parent group from being dragged
    this.draggedElement = event.currentTarget
    this.draggedType = 'group-item'
    event.currentTarget.classList.add('dragging')
    event.dataTransfer.effectAllowed = 'move'
  }

  // Drop requirement into group
  dropInGroup(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (!this.draggedElement) return
    
    // If it's a group item being reordered, just update (already handled by dragOverGroup)
    if (this.draggedElement.classList.contains('group-requirement-item')) {
      return
    }
    
    // Only allow requirement items (not groups)
    if (!this.draggedElement.classList.contains('requirement-item')) return
    
    const groupItems = event.currentTarget
    
    // Get requirement ID from hidden field or select
    let requirementId = null
    const hiddenField = this.draggedElement.querySelector('input[data-requirement-id]')
    if (hiddenField) {
      requirementId = hiddenField.dataset.requirementId || hiddenField.value
    }
    
    if (!requirementId) {
      const select = this.draggedElement.querySelector('.requirement-select')
      if (select && select.value) {
        requirementId = select.value
      }
    }
    
    // Get requirement name
    let requirementName = this.draggedElement.querySelector('.requirement-name')?.textContent
    if (!requirementName) {
      const select = this.draggedElement.querySelector('.requirement-select')
      if (select && select.value) {
        requirementName = select.options[select.selectedIndex]?.text
      }
    }
    
    if (!requirementId || !requirementName || requirementName === 'Select a Program') {
      console.log('Cannot add to group: requirement not selected or invalid')
      return
    }
    
    // Create group item element (no form fields needed - we'll build schema from DOM)
    const groupItem = document.createElement('div')
    groupItem.className = 'group-requirement-item'
    groupItem.dataset.requirementId = requirementId
    groupItem.draggable = true
    groupItem.dataset.action = 'dragstart->requirements#dragGroupItem dragend->requirements#dragEnd'
    groupItem.innerHTML = `
      <img src="${this.dragIconValue}" class="drag-icon-small" />
      <span>${requirementName}</span>
      <button type="button" class="btn-remove-from-group" data-action="click->requirements#removeFromGroup">×</button>
    `
    
    // Remove empty state if exists
    const emptyState = groupItems.querySelector('.group-empty-state')
    if (emptyState) emptyState.remove()
    
    groupItems.appendChild(groupItem)
    
    // Remove the original item from the main list
    this.draggedElement.remove()
    this.updateNumbers()
  }

  // Remove requirement from group
  removeFromGroup(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const groupItem = event.target.closest('.group-requirement-item')
    const groupItems = event.target.closest('.group-items')
    
    // Just remove it
    groupItem.remove()
    
    // Check if group is now empty
    const visibleItems = Array.from(groupItems.children).filter(child => 
      child.classList.contains('group-requirement-item')
    )
    if (visibleItems.length === 0) {
      groupItems.innerHTML = '<div class="group-empty-state"><p>Drag and drop requirements here</p></div>'
    }
  }

  getDragAfterElement(container, y) {
    const draggableElements = [...container.querySelectorAll('.requirement-item:not(.dragging), .requirement-group:not(.dragging)')]
    
    return draggableElements.reduce((closest, child) => {
      const box = child.getBoundingClientRect()
      const offset = y - box.top - box.height / 2
      
      if (offset < 0 && offset > closest.offset) {
        return { offset: offset, element: child }
      } else {
        return closest
      }
    }, { offset: Number.NEGATIVE_INFINITY }).element
  }

  getDragAfterGroupElement(container, y) {
    const draggableElements = [...container.querySelectorAll('.group-requirement-item:not(.dragging)')]
    
    return draggableElements.reduce((closest, child) => {
      const box = child.getBoundingClientRect()
      const offset = y - box.top - box.height / 2
      
      if (offset < 0 && offset > closest.offset) {
        return { offset: offset, element: child }
      } else {
        return closest
      }
    }, { offset: Number.NEGATIVE_INFINITY }).element
  }

  // Update requirement numbers
  updateNumbers() {
    const visibleItems = [...this.containerTarget.children].filter(child => 
      (child.classList.contains('requirement-item') || child.classList.contains('requirement-group')) &&
      child.style.display !== 'none'
    )
    
    visibleItems.forEach((item, index) => {
      const numberElement = item.querySelector('.requirement-number')
      if (numberElement) {
        numberElement.textContent = index + 1
      }
      
      // Update sort_position hidden field
      const sortInput = item.querySelector('input[name*="sort_position"]')
      if (sortInput) {
        sortInput.value = index
      }
    })
  }

  // Toggle dropdown menu
  toggleDropdown(event) {
    event.preventDefault()
    event.stopPropagation()
    const dropdown = event.currentTarget.nextElementSibling
    
    this.dropdownTargets.forEach(d => {
      if (d !== dropdown) {
        d.classList.remove('show')
      }
    })
    
    dropdown.classList.toggle('show')
  }

  // Close all dropdowns
  closeAllDropdowns(event) {
    if (event && event.target.closest('.requirement-menu-container')) return
    
    this.dropdownTargets.forEach(dropdown => {
      dropdown.classList.remove('show')
    })
  }
}
